import React from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import LocationVehiclePage from './LocationVehiclePage';

const seoConfig = {
  city: 'Decatur',
  state: 'TX',
  slug: 'decatur-tx',
  county: 'Wise County',
  title: 'Used Cars Decatur TX - Pre-Owned Trucks and Vehicles',
  h1: 'Used Cars and Trucks Near Decatur, TX',
  metaDescription: 'Used cars and trucks for sale near Decatur TX. 10 minutes south on 81/287. Full service shop on site. Financing for all credit types. Warranty included. Call (817) 382-2316.',
  ogTitle: 'Used Cars Near Decatur TX - Paradise-Performance',
  ogDescription: 'Decatur\'s closest used car dealer with a real service shop. 10 minutes on 287, thousands less than DFW lots.',
  ctaHeadline: '10 Minutes From the Courthouse',
  ctaSubline: 'Head south on 287, turn onto FM 1655. That\'s it. No tolls, no traffic, no games.',
  faqs: [
    {
      q: 'How do I get to Paradise-Performance from Decatur?',
      a: 'Head south on US-81/287, turn onto FM 1655 toward Paradise. About 10 minutes total. No highway, no tolls. Straight shot from the courthouse area.'
    },
    {
      q: 'What vehicles do you have for Decatur buyers?',
      a: 'Used trucks, SUVs, and sedans. We carry everyday vehicles for families plus specialty stuff like European cars. Inventory changes weekly.'
    },
    {
      q: 'Do you have a service shop for repairs after I buy?',
      a: 'Yes. Full repair shop on the same property. Oil changes, brakes, engine work, European car diagnostics, diesel service. Buy here, fix here. No getting sent to Fort Worth.'
    },
    {
      q: 'Is financing available if I have bad credit?',
      a: 'We finance every credit situation. Bad credit, no credit, self-employed, first-time buyers. We work with multiple lenders. Most approvals happen same day.'
    },
    {
      q: 'Do you take trade-ins from Decatur?',
      a: 'Yes. Bring your current vehicle and we\'ll give you a fair market appraisal on the spot. Trade value gets applied directly toward your purchase.'
    }
  ]
};

const DecaturTX = () => (
  <LocationVehiclePage config={seoConfig}>

    {/* ─── Hero ─── */}
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 30% 40%, rgba(217,119,6,0.15) 0%, transparent 50%)'
        }} />
      </div>
      <div className="relative container mx-auto px-4">
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Used Cars and Trucks Near Decatur, TX
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl">
            Ten minutes south of the Wise County courthouse. Better vehicles. Better prices. A real shop backing every sale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/inventory"
              className="inline-flex items-center justify-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-lg transition-colors"
            >
              View Inventory <FiArrowRight className="ml-2" />
            </Link>
            <a
              href="tel:+18173822316"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors"
            >
              <FiPhone className="mr-2" /> (817) 382-2316
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The Decatur Problem ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Decatur, TX</p>
          <h2 className="text-3xl font-bold mb-6">Decatur's Got Car Lots. Here's Why People Leave.</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>Decatur has a handful of lots along 287. Good people running most of them. But they price like they're competing with Fort Worth — because that's who they're benchmarking against. Same KBB markups. Same add-on fees. Same finance office upsells.</p>
            <p>We're 10 minutes south in Paradise. Our lot sits on a county road. No commercial lease. No showroom with granite countertops. That overhead difference shows up on the sticker. Most buyers save a couple thousand just on the drive.</p>
            <p>The bigger difference? We have a shop. Not a detail bay. A full repair shop with lifts, diagnostic equipment and mechanics who work on everything from Audis to diesel trucks. When you buy from us, warranty work gets handled right here. Not at some third-party shop in Denton.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── What Decatur Families Need ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">The Reality</p>
          <h2 className="text-3xl font-bold mb-6">What Decatur Families Actually Need</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>Decatur's the county seat. People here commute — south into DFW, north to Bridgeport and Jacksboro, east toward Denton. The morning 287 run is real. You need a vehicle that handles highway miles without draining your bank account at the pump.</p>
            <p>We keep fuel-efficient sedans and crossovers for the daily grind. SUVs for the family hauling kids to Decatur ISD. Trucks for people who need a bed, not a fashion statement. Most of our inventory sits under $20K because that's what real budgets look like.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Service After the Sale ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">We Don't Just Sell It and Wave Goodbye</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>This is the part most Decatur lots can't match. We run a <Link to="/services" className="text-amber-600 hover:underline">full-service repair shop</Link> — same property, same team. Buy a truck from us on Monday. Get your oil changed here the next month. Brakes start squeaking? Bring it in. Check engine light? We'll diagnose it.</p>
            <p>We do European car work too. BMW, Audi, Mercedes, Volkswagen, Porsche — factory-level diagnostics. Most shops in Wise County send that stuff to Fort Worth. We handle it in-house.</p>
            <p>Every vehicle we sell comes with a warranty. And that warranty gets serviced right here. Not "call this 800 number and find an approved facility." Here. By the guys who sold it to you.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Where Decatur Buyers Live ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2 text-center">Local Service</p>
          <h2 className="text-3xl font-bold text-center mb-10">Decatur Area Neighborhoods We Serve</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Courthouse / Downtown</h3>
              <p className="text-gray-600">Heart of Decatur. The historic square, the neighborhoods around it. People here need daily drivers and weekend trucks. Ten minutes from our lot.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">287 Corridor South</h3>
              <p className="text-gray-600">The stretch from Decatur toward Rhome and Boyd. Growing fast. New homes, young families. Reliable SUVs and commuter cars sell well here.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">FM 51 / Alvord Road</h3>
              <p className="text-gray-600">North of Decatur toward Alvord. Ranch country. These buyers need trucks that work — towing, hauling, dirt roads. Not a city cruiser.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">380 East (Toward Denton)</h3>
              <p className="text-gray-600">The commuter corridor. People living in Decatur for affordability, working in Denton or the north DFW suburbs. Gas mileage matters here.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Greenwood / Aurora</h3>
              <p className="text-gray-600">Small communities south of Decatur on the way to us. Some of our most loyal repeat customers live out this way.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Newark & Rhome</h3>
              <p className="text-gray-600">Just south on 287. Between us and the DFW sprawl. Families here get the best of both worlds — rural living, metro jobs.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Financing Section ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-center">Financing for All Credit Situations</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg text-center">
            <p>We work with lenders who understand small-town Texas. Seasonal income from ranch work? 1099 contractor? First vehicle out of high school? We've financed all of them.</p>
            <p>Most approvals come back same day. No games. No bait-and-switch on the rate. You'll know what your payment is before you sign anything.</p>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {['Bad Credit', 'No Credit', 'First-Time Buyers', 'Self-Employed'].map(item => (
              <div key={item} className="flex items-center bg-green-50 rounded-lg p-3">
                <FiCheckCircle className="text-green-600 mr-2 flex-shrink-0" />
                <span className="text-gray-700 font-medium text-sm">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/finance-application"
              className="inline-flex items-center px-8 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
            >
              Apply for Financing <FiArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Area Cross-Links ─── */}
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Serving All of Wise County and Beyond</h2>
          <p className="text-center text-gray-600 mb-6">We're in Paradise — right in the middle of everything. Wherever you live in the county, we're a short drive away.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/used-cars-bridgeport-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Bridgeport</h3>
              <p className="text-gray-500 text-sm">Lake life trucks. 6 min away.</p>
            </Link>
            <Link to="/used-cars-wise-county" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Wise County</h3>
              <p className="text-gray-500 text-sm">Born and built here. Every town in reach.</p>
            </Link>
            <Link to="/used-cars-weatherford-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Weatherford</h3>
              <p className="text-gray-500 text-sm">Skip the I-20 markup. 30 min north.</p>
            </Link>
            <Link to="/used-cars-denton-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Denton</h3>
              <p className="text-gray-500 text-sm">380 straight to us. European car focus.</p>
            </Link>
          </div>
        </div>
      </div>
    </section>

  </LocationVehiclePage>
);

export default DecaturTX;
